import { computed, type MaybeRefOrGetter, toValue } from 'vue'

import { useLibrary } from '@courses/library-layer/composables/useLibrary'

import { useLocation } from '@common/location-layer/composables/useLocation'

import { useProductQueryStore } from '../stores/product.query-store'
import { useProductUIStore } from '../stores/product.ui-store'

export const useProduct = (courseLocationId?: MaybeRefOrGetter<string | undefined>) => {
  const { locationId } = useLocation()

  const { fetchAllProducts } = useLibrary(locationId)
  const queryStore = useProductQueryStore(courseLocationId)

  const uiStore = useProductUIStore()

  // Initialize queries during setup phase

  function fetchProduct(id: MaybeRefOrGetter<string>) {
    try {
      const { data: products, isLoading: isProductsLoading } = fetchAllProducts()

      return {
        data: computed(() => {
          const currentId = toValue(id)
          return products.value?.find(p => p.id === currentId)
        }),
        isLoading: computed(() => isProductsLoading.value),
        error: computed(() => null),
        suspense: () => Promise.resolve(),
      }
    } catch (error) {
      console.error('Failed to fetch product:', error)
      // fallback to query store
      const productQuery = queryStore.fetchProductQuery(id)
      return productQuery
    }
  }

  function fetchResolvedProductInfo(productId: MaybeRefOrGetter<string>) {
    try {
      return queryStore.fetchResolvedProductInfoQuery(productId)
    } catch (error) {
      console.error('Failed to fetch resolved product info:', error)
      throw error
    }
  }

  /** Detail endpoint; returns full `customizations` (incl. `generalSettings`) that the list omits. */
  function fetchProductDetail(id: MaybeRefOrGetter<string>) {
    try {
      return queryStore.fetchProductQuery(id)
    } catch (error) {
      console.error('Failed to fetch product detail:', error)
      throw error
    }
  }

  function fetchProductDataFromOffer(productId: MaybeRefOrGetter<string>) {
    try {
      const productDataFromOfferQuery = queryStore.fetchProductDataFromOfferQuery(productId)
      return productDataFromOfferQuery
    } catch (error) {
      console.error('Failed to fetch product data from offer:', error)
      throw error
    }
  }

  function fetchPinnedProducts() {
    try {
      const pinnedProductsQuery = queryStore.fetchPinnedProductsQuery()
      return pinnedProductsQuery
    } catch (error) {
      console.error('Failed to fetch pinned products:', error)
      throw error
    }
  }

  function pinProduct(productId: MaybeRefOrGetter<string>) {
    try {
      const pinProductMutation = queryStore.pinProductMutation.mutateAsync(toValue(productId))
      return pinProductMutation
    } catch (error) {
      console.error('Failed to pin product:', error)
      throw error
    }
  }

  function unpinProduct(productId: MaybeRefOrGetter<string>) {
    try {
      const unpinProductMutation = queryStore.unpinProductMutation.mutateAsync(toValue(productId))
      return unpinProductMutation
    } catch (error) {
      console.error('Failed to unpin product:', error)
      throw error
    }
  }

  function fetchCustomizationSettings(productId: MaybeRefOrGetter<string>, params?: Record<string, unknown>) {
    try {
      const customizationQuery = queryStore.fetchCustomizationSettingsQuery(productId, params)
      return customizationQuery
    } catch (error) {
      console.error('Failed to fetch customization settings:', error)
      throw error
    }
  }

  function fetchProductTemplateByProductId(productId: MaybeRefOrGetter<string>) {
    try {
      const templateQuery = queryStore.fetchProductTemplateByProductIdQuery(productId)
      return templateQuery
    } catch (error) {
      console.error('Failed to fetch product template by product ID:', error)
      throw error
    }
  }

  function checkIfProductThemeExists(productId: string) {
    try {
      const themeExistsQuery = queryStore.checkIfProductThemeExistsQuery(productId)
      return themeExistsQuery
    } catch (error) {
      console.error('Failed to check if product theme exists:', error)
      throw error
    }
  }

  function fetchProductAccess(
    productId: MaybeRefOrGetter<string>,
    source: MaybeRefOrGetter<string>,
    queryOptions?: { enabled?: MaybeRefOrGetter<boolean> }
  ) {
    try {
      return queryStore.fetchProductAccessQuery(productId, source, queryOptions)
    } catch (error) {
      console.error('Failed to fetch product access:', error)
      throw error
    }
  }

  function fetchProductValidity(productId: MaybeRefOrGetter<string>, enabled: MaybeRefOrGetter<boolean>) {
    try {
      return queryStore.fetchProductValidityQuery(productId, enabled)
    } catch (error) {
      console.error('Failed to fetch product validity:', error)
      throw error
    }
  }

  function fetchCompleteProduct(productId: MaybeRefOrGetter<string>) {
    try {
      return queryStore.fetchCompleteProductQuery(productId)
    } catch (error) {
      console.error('Failed to fetch complete product:', error)
      throw error
    }
  }

  function fetchProductTemplateById(templateId: string, productId: string) {
    try {
      const templateQuery = queryStore.fetchProductTemplateByIdQuery(templateId, productId)
      return templateQuery
    } catch (error) {
      console.error('Failed to fetch product template by ID:', error)
      throw error
    }
  }

  return {
    store: uiStore,
    fetchProduct,
    fetchProductDetail,
    fetchPinnedProducts,
    pinProduct,
    unpinProduct,
    fetchCustomizationSettings,
    fetchProductTemplateByProductId,
    checkIfProductThemeExists,
    fetchProductTemplateById,
    fetchProductDataFromOffer,
    fetchResolvedProductInfo,
    fetchProductAccess,
    fetchProductValidity,
    fetchCompleteProduct,
  }
}
